import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { BookOpen, ChevronDown, ChevronUp, Lightbulb, CheckCircle, XCircle, MessageSquare } from 'lucide-react'

// 面试题库数据
const INTERVIEW_QUESTIONS = {
  values: {
    title: '价值观面试问题',
    icon: CheckCircle,
    questions: [
      {
        question: '你如何看待集体利益和个人利益的关系？',
        logic: '考察候选人的价值观是否与央国企文化契合',
        keyPoints: ['强调集体利益优先', '个人利益在集体框架内实现', '举例说明'],
        goodAnswer: '我认为集体利益和个人利益是统一的。在央国企工作，首先要服从组织安排，在集体发展中实现个人价值。',
        badAnswer: '我觉得个人利益更重要，如果集体影响我个人发展，我会优先考虑自己。'
      },
      {
        question: '你对加班怎么看？',
        logic: '考察工作态度和奉献精神',
        keyPoints: ['接受合理加班', '强调效率优先', '特殊情况服从安排'],
        goodAnswer: '我认为工作效率是第一位的。如果是因为紧急任务或项目节点需要加班，我绝对服从安排。',
        badAnswer: '我坚决反对加班，工作生活必须严格分开。'
      },
      {
        question: '如果领导安排的工作与你的专业不符，你会怎么办？',
        logic: '考察服从意识和适应能力',
        keyPoints: ['服从安排', '积极学习', '寻求支持'],
        goodAnswer: '我会服从领导安排，同时积极学习相关知识，尽快胜任工作。如有困难会及时沟通。',
        badAnswer: '我会直接拒绝，只做我专业范围内的工作。'
      }
    ]
  },
  ability: {
    title: '综合能力面试问题',
    icon: Lightbulb,
    questions: [
      {
        question: '描述一次你解决复杂问题的经历',
        logic: '考察问题解决能力和思维逻辑',
        keyPoints: ['STAR法则描述', '突出分析过程', '量化结果'],
        goodAnswer: '在XX项目中，我负责协调多个部门。通过建立沟通机制、明确责任分工，最终提前一周完成任务。',
        badAnswer: '我没遇到过什么复杂问题。'
      },
      {
        question: '你如何处理与同事的分歧？',
        logic: '考察沟通协调能力',
        keyPoints: ['尊重对方', '求同存异', '寻求上级协调'],
        goodAnswer: '首先倾听对方观点，寻找共同点。如果无法达成一致，会请示领导协调。',
        badAnswer: '我会坚持己见，让对方接受我的观点。'
      }
    ]
  },
  quality: {
    title: '素质面试问题',
    icon: MessageSquare,
    questions: [
      {
        question: '你的优点和缺点是什么？',
        logic: '考察自我认知',
        keyPoints: ['优点与岗位匹配', '缺点可改进', '有改进措施'],
        goodAnswer: '优点是做事认真负责，缺点是有时过于追求完美。我正在学习把握重点，提高效率。',
        badAnswer: '我没有缺点。'
      }
    ]
  },
  professional: {
    title: '专业面试问题',
    icon: BookOpen,
    questions: [
      {
        question: '请简述你对本行业发展趋势的理解',
        logic: '考察行业认知',
        keyPoints: ['宏观政策', '行业动态', '个人见解'],
        goodAnswer: '结合国家政策和行业报告，分析数字化转型、绿色发展等趋势...',
        badAnswer: '我不太关注行业趋势。'
      }
    ]
  },
  career: {
    title: '职业规划问题',
    icon: CheckCircle,
    questions: [
      {
        question: '未来3年你有什么规划？',
        logic: '考察职业稳定性',
        keyPoints: ['与企业发展结合', '具体可执行', '体现稳定性'],
        goodAnswer: '前1年熟悉业务，2-3年成为业务骨干，长期希望在企业内持续发展。',
        badAnswer: '我想先干两年，然后跳槽去更好的公司。'
      }
    ]
  }
}

// 经典词汇解读
const VOCABULARY = [
  {
    word: '政治站位',
    plainMeaning: '从政治高度看问题，站在党和国家的立场思考',
    purpose: '考察候选人的政治敏感性和大局观',
    scenario: '在回答涉及政策、改革类问题时使用',
    soul: '不是让你喊口号，而是让你证明你能和央国企同频共振'
  },
  {
    word: '大局意识',
    plainMeaning: '不只看自己一亩三分地，要看到整体',
    purpose: '考察是否具备统筹协调能力',
    scenario: '处理跨部门协作、资源冲突时使用',
    soul: '央国企最怕\"各扫门前雪\"的人'
  },
  {
    word: '责任担当',
    plainMeaning: '敢扛事、能扛事、扛成事',
    purpose: '考察责任感和执行力',
    scenario: '描述项目经历、困难处理时使用',
    soul: '不是背锅，而是让领导放心把事交给你'
  },
  {
    word: '服务意识',
    plainMeaning: '把自己定位为服务者而非管理者',
    purpose: '考察角色认知',
    scenario: '回答\"如何看自己的岗位\"时使用',
    soul: '央国企的本质是服务国家、服务人民'
  },
  {
    word: '底线思维',
    plainMeaning: '做最坏的打算，争取最好的结果',
    purpose: '考察风险意识',
    scenario: '回答风险管理、合规类问题时使用',
    soul: '央国企不能出事，这是铁律'
  }
]

const QuestionCard = ({ question, index }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      className="border border-gray-200 rounded-lg overflow-hidden">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
        <span className="font-medium text-gray-900 text-left">{question.question}</span>
        {isOpen ? <ChevronUp size={18} className="text-gray-400 shrink-0" /> : <ChevronDown size={18} className="text-gray-400 shrink-0" />}
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-100">
            <div className="p-4 space-y-3">
              <div>
                <span className="text-xs font-bold text-primary-600">考察逻辑：</span>
                <p className="text-sm text-gray-600 mt-1">{question.logic}</p>
              </div>
              <div>
                <span className="text-xs font-bold text-primary-600">回答要点：</span>
                <ul className="text-sm text-gray-600 mt-1 list-disc list-inside">
                  {question.keyPoints.map((point, i) => <li key={i}>{point}</li>)}
                </ul>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <span className="text-xs font-bold text-green-700">加分回答：</span>
                <p className="text-sm text-green-800 mt-1">{question.goodAnswer}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <span className="text-xs font-bold text-red-700">减分回答：</span>
                <p className="text-sm text-red-800 mt-1">{question.badAnswer}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

const InterviewPage = () => {
  const [activeTab, setActiveTab] = useState('questions')
  const [activeCategory, setActiveCategory] = useState('values')

  const categories = Object.entries(INTERVIEW_QUESTIONS)

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
        <h1 className="section-title">面试题库</h1>
        <p className="section-subtitle">央国企面试常见问题与答题策略</p>
      </motion.div>

      {/* Tab切换 */}
      <div className="flex space-x-4 mb-6">
        <button onClick={() => setActiveTab('questions')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'questions' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          面试问题
        </button>
        <button onClick={() => setActiveTab('vocabulary')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'vocabulary' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          经典词汇
        </button>
      </div>

      {activeTab === 'questions' ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {/* 分类选择 */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map(([key, category]) => (
              <button key={key} onClick={() => setActiveCategory(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {category.title}
              </button>
            ))}
          </div>

          {/* 问题列表 */}
          <div className="space-y-3">
            {INTERVIEW_QUESTIONS[activeCategory].questions.map((q, i) => (
              <QuestionCard key={i} question={q} index={i} />
            ))}
          </div>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid md:grid-cols-2 gap-4">
          {VOCABULARY.map((item, index) => (
            <motion.div key={item.word} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}
              className="card p-5">
              <h3 className="text-lg font-bold text-primary-700 mb-3">{item.word}</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700">大白话：</span>
                  <p className="text-gray-600">{item.plainMeaning}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">考察目的：</span>
                  <p className="text-gray-600">{item.purpose}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">使用场景：</span>
                  <p className="text-gray-600">{item.scenario}</p>
                </div>
                <div className="p-3 bg-primary-50 rounded-lg mt-3">
                  <span className="font-medium text-primary-700">灵魂解读：</span>
                  <p className="text-primary-600 italic mt-1">"{item.soul}"</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  )
}

export default InterviewPage
